import { RxCollection, RxDatabase, RxDocument } from 'rxdb'
import { DateTime } from 'luxon'
import { LOGGER } from '@cicero/data/utils'
import { IModelSchema, IModelSchemaConfig } from './schema'
import { EntityNotFoundError, WrongParamsError } from './errors'
import { BaseSchema } from './BaseSchema'

/**
 * Creates a collection from DB schema
 * @param db 
 * @param schema 
 */
export const CREATE_COLLECTION = async <T, M, CM>(db: RxDatabase, schema: IModelSchema<T, M, CM>): Promise<RxCollection<T, M, CM>> => {
  const coll = await db.collection<T, M, CM>(schema as any)
  // coll.$.subscribe(change => LOGGER.debug(`[${schema.name.toUpperCase()}_${change.operation.toUpperCase()}]`, change))
  return coll
}

export const GENERATE_ID = () => `${Math.random()}`
export const IS_OFFLINE_ID = (id: string) => /[0-9]\.[0-9]+/.test(id)
export const IS_TEMPORARY = (doc: RxDocument<any>) => {
  return doc._isTemporary
}

/**
 * Given a schema, attach allowed params to a new or existing doc object
 * @param config 
 * @param params 
 * @param doc 
 * @todo recurse if a key in schema is an object
 * @todo allow decapitalization here?
 */
export const MERGE_ALLOWED_PARAMS_INTO_DOC = (config: IModelSchemaConfig, params: any, doc: any = {}) => {
  if (!config.initialized) { LOGGER.warn('attempting to handle param merging in an unitialized model!', { config, params, doc }) }
  const properties = config.properties || {}
  const nonExistentKeys: string[] = []
  const parsedDoc = Object.keys(params).reduce((obj: any, key: string) => {
    if (properties[key] === undefined) {
      nonExistentKeys.push(key)
    } else {
      obj[key] = params[key]
      // TODO If value is object, recurse
    }
    return obj
  }, doc)

  if (nonExistentKeys.length > 0 && process.env.NODE_ENV !== 'test') {
    LOGGER.warn(`${nonExistentKeys.length} keys from an API response do not exist in the schema`, { config, nonExistentKeys, doc, params })
  }

  return parsedDoc
}

/**
 * Make sure a doc is ready to save to the store, based on whether it is new, or loaded from the API
 * @todo if deleted?
 */
export const PREPARE_DOC_FOR_SAVE = (doc: Partial<BaseSchema>) => {
  const t = Date.now()
  let addTimestamps = false
  if (!doc.meta) { doc.meta = {} }
  if (!doc.id) {
    doc.id = GENERATE_ID()
    doc.meta.created_offline_at = t // Neither id or uuid means a new doc created offline
    doc.meta.updated_offline_at = t // Also update offline now
    addTimestamps = true
  } else if (doc.meta.fresh) { // We use set fresh when we are saving API data to ensure no further sync infinite loops are triggered.
    delete doc.meta.fresh
    doc.meta.last_synced_at = t
  } else { // All other changes are considered local, so we change the updated stamp
    doc.meta.updated_offline_at = t
    addTimestamps = true
  }

  if (addTimestamps) {
    const d = DateTime.fromMillis(t)
    if (!doc.created_at) { doc.created_at = d.toISO() }
    doc.updated_at = d.toISO()
  }

  return doc
}

// GET functions only look up in a local collection. All return an RxDocument
export const GET_BY_KEY_VALUE = async <T = any>(
  c: RxCollection<T>,
  key: string,
  value: any
) => c.findOne().where(key).eq(value)

// Get by ID, permissive of a meta.uuid value too, if the id isn't a local one
export const GET_BY_ID = async <T = any>(c: RxCollection<T>, id: string) => {
  const docIfExists = await c.findOne(id).exec()
  if (docIfExists === null && !IS_OFFLINE_ID(id)) {
    return c.findOne({ selector: { 'meta.uuid': id } }).exec()
  }
  return docIfExists
}

/**
 * Performs a query against a store
 * @param c 
 * @param params 
 */
export const QUERY_COLLECTION = (c: RxCollection, params: any) => { // TODO Strongly Type Params
  let query = c.find()
  for (const key in params) {
    query = query.where(key).eq(params[key]) // TODO Better handle custom params
  }
  return query
}

export const DELETE_DOC = async <T>(c: RxCollection, docOrId: string|undefined|RxDocument<T>) => {
  let doc: RxDocument<T>
  switch (typeof docOrId) {
    case 'string':
      doc = await c.findOne().where('id').equals(docOrId).exec()
      if (doc) {
        docOrId = doc
      } else {
        throw new EntityNotFoundError('Cannot find entity to delete', { id: docOrId })
      }
      break

    case 'undefined':
      throw new WrongParamsError('Passed invalid doc id to delete', { docOrId, collection: c })

    default:
      // TODO if doc has no id, there is nothing to delete
      doc = docOrId
  }

  if (IS_TEMPORARY(doc)) { throw new Error('Attempting to delete unsaved entity') }
  return doc.atomicSet('meta.deleted_offline_at', Date.now())
}

export const REMOVE_DOC = async (c: RxCollection, id: string) => {
  const doc = await c.findOne().where('id').eq(id).exec()
  if (doc) {
    return doc.remove()
  } else {
    LOGGER.warn('tried to remove doc that does not exist')
  }
}
